import { type FmcsaCargo } from '@/components/freight/FmcsaCargoHints'
import { IllustrationName } from '@/components/icons/Illustration'
import { Separator } from '@/components/ui/separator'
import { type SuggestionGroup } from '@/components/ui/tag-input'
import { type CommodityItem } from '@/models/user'
import { toCamelCase } from '../../lib/typeHelper'
import type { ChatContent, ChatContentNested, ChatItem } from '../../models/chat'
import { CONFIRM_INFO_PATH } from '../../models/confirmInfo'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { EnabledCoverage } from '../../models/insuranceCoverage'
import type { OwnerAsDriver } from '../DriversTable'
import { ChatAddCoverage } from './ChatAddCoverage'
import { ChatAddDrivers } from './ChatAddDrivers'
import { ChatAddEquipments } from './ChatAddEquipments'
import { ChatAddTelematics } from './ChatAddTelematics'
import { ChatButtonGroup, ChatButtonGroupProps } from './ChatButtonGroup'
import { ChatCardCheckbox, ChatCardCheckboxProps } from './ChatCardCheckbox'
import { ChatComposer } from './ChatComposer'
import { ChatConfirmInfoLink } from './ChatConfirmInfoLink'
import { ChatConfirmInformation } from './ChatConfirmInformation'
import { ChatCoverageStartDate } from './ChatCoverageStartDate'
import { ChatDotNumber } from './ChatDotNumber'
import { ChatEarlyIndication, ChatEarlyIndicationProps } from './ChatEarlyIndication'
import { ChatFileUpload } from './ChatFileUpload'
import { ChatFreightAllocation } from './ChatFreightAllocation'
import { ChatInfoPopover } from './ChatInfoPopover'
import { ChatInput } from './ChatInput'
import { ChatLink } from './ChatLink'
import { ChatLossRunForm } from './ChatLossRunForm'
import { ChatMarkdown } from './ChatMarkdown'
import { ChatOtp } from './ChatOtp'
import { ChatOwnerOperatorDriver } from './ChatOwnerOperatorDriver'
import { ChatOwnerOperatorTruck } from './ChatOwnerOperatorTruck'
import { ChatSignAgreement } from './ChatSignAgreement'
import { ChatVerifyEmail } from './ChatVerifyEmail'
import { TypingDots } from './TypingIndicator'

interface ChatMessageComponentProps {
  enableMarkdown?: boolean
  content: ChatContentNested
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  orgId?: string
  readonly?: boolean
  dotNumber?: string
  /** Set while this message's text is still streaming in — see ChatMarkdown. */
  streaming?: boolean
}

export function ChatMessageComponent({
  content,
  handleSubmit,
  enableMarkdown,
  orgId,
  readonly,
  dotNumber,
  streaming,
}: ChatMessageComponentProps) {
  if (typeof content === 'string') {
    if (enableMarkdown) return <ChatMarkdown content={content} streaming={streaming} />
    return <span className="whitespace-pre-wrap">{content}</span>
  }

  if (typeof content === 'object' && Array.isArray(content)) {
    return (
      <>
        {content.map((item, index) => (
          <ChatMessageComponent
            key={item.id || item.clientId || index}
            content={item}
            handleSubmit={handleSubmit}
            enableMarkdown={enableMarkdown}
            streaming={streaming}
            orgId={orgId}
            readonly={readonly}
            dotNumber={dotNumber}
          />
        ))}
      </>
    )
  }

  const contentObj = content as ChatContent | null

  if (!contentObj) {
    return null
  }

  if (contentObj.component === 'P') {
    const { info, ...paragraphProps } = (contentObj.props ?? {}) as { info?: string } & Record<
      string,
      unknown
    >

    if (info) {
      return (
        <div {...paragraphProps} className="flex items-center gap-1">
          <ChatMessageComponent
            content={contentObj.content}
            handleSubmit={handleSubmit}
            enableMarkdown={enableMarkdown}
            streaming={streaming}
            orgId={orgId}
            readonly={readonly}
            dotNumber={dotNumber}
          />{' '}
          <span className="inline">
            <ChatInfoPopover text={info} />
          </span>
        </div>
      )
    }

    return (
      <div {...paragraphProps}>
        <ChatMessageComponent
          content={contentObj.content}
          handleSubmit={handleSubmit}
          enableMarkdown={enableMarkdown}
          streaming={streaming}
          orgId={orgId}
          readonly={readonly}
          dotNumber={dotNumber}
        />
      </div>
    )
  }

  if (contentObj.component === 'DIVIDER') {
    return <Separator />
  }

  if (contentObj.component === 'UL') {
    return (
      <ul className="mb-0 ml-6 list-disc">
        <ChatMessageComponent
          content={contentObj.content}
          handleSubmit={handleSubmit}
          enableMarkdown={enableMarkdown}
          streaming={streaming}
          orgId={orgId}
          readonly={readonly}
          dotNumber={dotNumber}
        />
      </ul>
    )
  }

  if (contentObj.component === 'LI') {
    return (
      <li className="mt-1">
        <ChatMessageComponent
          content={contentObj.content}
          handleSubmit={handleSubmit}
          enableMarkdown={enableMarkdown}
          streaming={streaming}
          orgId={orgId}
          readonly={readonly}
          dotNumber={dotNumber}
        />
      </li>
    )
  }

  if (contentObj.component === 'INPUT') {
    return (
      <ChatInput
        handleSubmit={handleSubmit}
        service={contentObj.props?.service as string}
        type={contentObj.props?.type as string}
        readonly={readonly}
        initialValue={contentObj.props?.value as string | undefined}
        disclosure={contentObj.props?.disclosure as string | undefined}
        placeholder={contentObj.props?.placeholder as string | undefined}
        multiline={contentObj.props?.multiline as boolean | undefined}
      />
    )
  }

  if (contentObj.component === 'TEXTAREA') {
    // Only for frontends from before the freight editor, which skip the editor and answer here.
    if (contentObj.props?.legacyFallback) return null
    return (
      <ChatComposer
        content={contentObj}
        handleSubmit={handleSubmit}
        service={contentObj.props?.service as string}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'DOT_NUMBER') {
    return (
      <ChatDotNumber
        maxLength={contentObj.props?.maxLength as number | undefined}
        placeholder={contentObj.props?.placeholder as string | undefined}
        noDotHref={contentObj.props?.noDotHref as string | undefined}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  // Legacy DOT entry — kept so chat items stored before DOT_NUMBER still render.
  if (contentObj.component === 'OTP') {
    return (
      <ChatOtp
        length={contentObj.props?.length as number}
        prefix={contentObj.props?.prefix as string}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'VERIFY_EMAIL') {
    return (
      <ChatVerifyEmail
        email={contentObj.props?.email as string}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'BUTTON_GROUP') {
    return (
      <ChatButtonGroup
        buttons={contentObj.props?.buttons as ChatButtonGroupProps['buttons']}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'CARD_CHECKBOX') {
    return (
      <ChatCardCheckbox
        options={contentObj.props?.options as ChatCardCheckboxProps['options']}
        columns={contentObj.props?.columns as number}
        handleSubmit={handleSubmit}
        readonly={readonly}
        defaultSelected={contentObj.props?.defaultSelected as string[]}
        singleSelect={contentObj.props?.singleSelect as boolean}
        allowOther={contentObj.props?.allowOther as boolean}
      />
    )
  }

  if (contentObj.component === 'FILE_UPLOAD') {
    return (
      <ChatFileUpload
        accept={contentObj.props?.accept as string}
        handleSubmit={handleSubmit}
        multiple={contentObj.props?.multiple as boolean}
        label={contentObj.props?.label as string}
        readonly={readonly || (contentObj.props?.readonly as boolean)}
        illustration={contentObj.props?.illustration as IllustrationName | undefined}
      />
    )
  }

  if (contentObj.component === 'ADD_TELEMATICS') {
    return (
      <ChatAddTelematics
        handleSubmit={handleSubmit}
        label={contentObj.props?.label as string}
        isPostBind={contentObj.props?.isPostBind as boolean}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'ADD_DRIVERS' && contentObj.props?.ownerOperator && !contentObj.props.edit) {
    return (
      <ChatOwnerOperatorDriver
        driverId={(contentObj.props.driverId as string | null) ?? null}
        prefill={(contentObj.props.prefill as OwnerAsDriver | null) ?? null}
        handleSubmit={handleSubmit}
        orgId={orgId}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'ADD_DRIVERS') {
    return (
      <ChatAddDrivers
        edit={contentObj.props?.edit as boolean}
        handleSubmit={handleSubmit}
        orgId={orgId}
        readonly={readonly}
      />
    )
  }

  if (
    contentObj.component === 'ADD_EQUIPMENTS' &&
    contentObj.props?.type === 'trucks' &&
    contentObj.props.ownerOperator &&
    !contentObj.props.edit
  ) {
    return (
      <ChatOwnerOperatorTruck
        truckId={(contentObj.props.truckId as string | null) ?? null}
        handleSubmit={handleSubmit}
        orgId={orgId}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'ADD_EQUIPMENTS') {
    return (
      <ChatAddEquipments
        edit={contentObj.props?.edit as boolean}
        handleSubmit={handleSubmit}
        type={contentObj.props?.type as 'trucks' | 'trailers'}
        orgId={orgId}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'COVERAGE_START_DATE') {
    return (
      <ChatCoverageStartDate
        defaultDate={
          typeof contentObj.props?.defaultDate === 'string' ? contentObj.props.defaultDate : undefined
        }
        isFallbackDefault={contentObj.props?.isFallbackDefault !== false}
        minDate={typeof contentObj.props?.minDate === 'string' ? contentObj.props.minDate : undefined}
        maxDate={typeof contentObj.props?.maxDate === 'string' ? contentObj.props.maxDate : undefined}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'ADD_INSURANCE_COVERAGE') {
    return (
      <ChatAddCoverage
        coverages={(contentObj.props?.coverages as ReadonlyArray<EnabledCoverage>)?.map(
          (coverage) => toCamelCase(coverage) as EnabledCoverage
        )}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'SIGN_AGREEMENT') {
    return (
      <ChatSignAgreement
        label={contentObj.props?.label as string}
        handleSubmit={handleSubmit}
        isSigned={readonly || (contentObj.props?.isSigned as boolean)}
        envelopeId={contentObj.props?.envelopeId as string | undefined}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'LOSS_RUN_FORM') {
    return (
      <ChatLossRunForm
        handleSubmit={handleSubmit}
        readonly={readonly}
        defaultValues={contentObj.props?.defaultValues as never}
        isParsing={contentObj.props?.isParsing as boolean | undefined}
      />
    )
  }

  if (contentObj.component === 'FREIGHT_ALLOCATION') {
    return (
      <ChatFreightAllocation
        suggestions={(contentObj.props?.suggestions as string[] | undefined) ?? []}
        suggestionGroups={(contentObj.props?.suggestionGroups as SuggestionGroup[] | undefined) ?? []}
        fmcsaCargo={(contentObj.props?.fmcsaCargo as FmcsaCargo[] | undefined) ?? []}
        defaultValue={(contentObj.props?.defaultValue as CommodityItem[] | undefined) ?? []}
        orgId={orgId}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'LINK') {
    const href = contentObj.props?.href as string
    const label = contentObj.props?.label as string
    if (href === CONFIRM_INFO_PATH) return <ChatConfirmInfoLink orgId={orgId} label={label} />
    return <ChatLink href={href} label={label} />
  }

  if (contentObj.component === 'CONFIRM_INFORMATION') {
    return (
      <ChatConfirmInformation
        freightDetailsChat={contentObj.props?.freightDetailsChat as ChatItem[]}
        orgId={orgId}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'EARLY_INDICATION') {
    return (
      <ChatEarlyIndication
        coverages={contentObj.props?.coverages as ChatEarlyIndicationProps['coverages']}
        powerUnits={contentObj.props?.powerUnits as number}
        handleSubmit={handleSubmit}
        readonly={readonly}
      />
    )
  }

  if (contentObj.component === 'LOADING_EARLY_INDICATION' || contentObj.component === 'LOADING') {
    return (
      <div className="self-start">
        <TypingDots />
      </div>
    )
  }

  return null
}
